import React from 'react';
import { Zap, Timer, Volume2, VolumeX, Eye, EyeOff, Save, RefreshCw } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

const Settings = () => {
  const { settings, updateSettings, resetSettings } = useSettings();
  
  const handleDifficultyChange = (difficulty) => {
    updateSettings({ difficulty });
  };
  
  const handleTimeLimitChange = (e) => {
    const timeLimit = parseInt(e.target.value, 10);
    updateSettings({ timeLimit });
  };
  
  const toggleSoundEffects = () => {
    updateSettings({ soundEffects: !settings.soundEffects });
  };
  
  const toggleShowHints = () => {
    updateSettings({ showHints: !settings.showHints });
  };
  
  const handleFontScaleChange = (e) => {
    const fontScale = parseFloat(e.target.value);
    updateSettings({ fontScale });
  };
  
  return (
    <div className="game-card">
      <h2 className="text-xl font-semibold mb-6 uyghur-text">ئويۇن تەڭشەكلىرى</h2>
      
      <div className="space-y-8">
        {/* Difficulty */}
        <div>
          <h3 className="text-lg font-medium mb-3 flex items-center uyghur-text">
            <Zap size={18} className="mr-2 text-yellow-500" />
            قىيىنلىق دەرىجىسى
          </h3>
          <div className="flex flex-wrap gap-3">
            <button
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                settings.difficulty === 'easy' 
                  ? 'bg-green-500 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => handleDifficultyChange('easy')}
            >
              <span className="uyghur-text">ئاسان</span>
            </button>
            <button
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                settings.difficulty === 'medium' 
                  ? 'bg-yellow-500 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => handleDifficultyChange('medium')}
            >
              <span className="uyghur-text">ئوتتۇرا</span>
            </button>
            <button
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                settings.difficulty === 'hard' 
                  ? 'bg-red-500 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => handleDifficultyChange('hard')}
            >
              <span className="uyghur-text">قىيىن</span>
            </button>
          </div>
          <p className="mt-2 text-sm text-gray-500 uyghur-text">
            {settings.difficulty === 'easy' && 'ئاسان: روبوت قىسقا سۆزلەرنى تاللايدۇ، داۋاملاشتۇرۇش ئاسان بولىدۇ.'}
            {settings.difficulty === 'medium' && 'ئوتتۇرا: تەڭپۇڭلاشتۇرۇلغان قىيىنلىق، خىلمۇخىل سۆزلەر.'}
            {settings.difficulty === 'hard' && 'قىيىن: روبوت قىيىن ھەرپلەر بىلەن ئاخىرلىشىدىغان سۆزلەرنى تاللاشقا تىرىشىدۇ.'}
          </p>
        </div>
        
        {/* Time Limit */}
        <div>
          <h3 className="text-lg font-medium mb-3 flex items-center uyghur-text">
            <Timer size={18} className="mr-2 text-blue-500" />
            ۋاقىت چەكلىمىسى
          </h3>
          <div>
            <input
              type="range"
              min="0"
              max="60"
              step="5"
              value={settings.timeLimit}
              onChange={handleTimeLimitChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0</span>
              <span>15</span>
              <span>30</span>
              <span>45</span>
              <span>60</span>
            </div>
            <p className="mt-2 text-center font-medium">
              {settings.timeLimit === 0 ? (
                <span className="uyghur-text">ۋاقىت چەكلىمىسى يوق</span>
              ) : (
                <span className="uyghur-text">{settings.timeLimit} سېكۇنت</span>
              )}
            </p>
          </div>
        </div>
        
        {/* Sound Effects */}
        <div>
          <h3 className="text-lg font-medium mb-3 flex items-center uyghur-text">
            {settings.soundEffects ? (
              <Volume2 size={18} className="mr-2 text-purple-500" />
            ) : (
              <VolumeX size={18} className="mr-2 text-gray-500" />
            )}
            ئاۋاز ئۈنۈملىرى
          </h3>
          <button
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              settings.soundEffects 
                ? 'bg-purple-100 text-purple-700 border border-purple-200' 
                : 'bg-gray-100 text-gray-700 border border-gray-200'
            }`}
            onClick={toggleSoundEffects}
          >
            {settings.soundEffects ? (
              <>
                <Volume2 size={18} />
                <span className="uyghur-text">ئاۋاز ئۈنۈملىرى ئېچىق</span>
              </>
            ) : (
              <>
                <VolumeX size={18} />
                <span className="uyghur-text">ئاۋاز ئۈنۈملىرى تاقاق</span>
              </>
            )}
          </button>
        </div>
        
        {/* Show Hints */}
        <div>
          <h3 className="text-lg font-medium mb-3 flex items-center uyghur-text">
            {settings.showHints ? (
              <Eye size={18} className="mr-2 text-emerald-500" />
            ) : (
              <EyeOff size={18} className="mr-2 text-gray-500" />
            )}
            سۆز ھەققىدە ئەسكەرتىش
          </h3>
          <button
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              settings.showHints 
                ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
                : 'bg-gray-100 text-gray-700 border border-gray-200'
            }`}
            onClick={toggleShowHints}
          >
            {settings.showHints ? (
              <>
                <Eye size={18} />
                <span className="uyghur-text">ئەسكەرتىش كۆرسىتىلسۇن</span>
              </>
            ) : (
              <>
                <EyeOff size={18} />
                <span className="uyghur-text">ئەسكەرتىش كۆرسىتىلمىسۇن</span>
              </>
            )}
          </button>
        </div>
        
        {/* Font Size */}
        <div>
          <h3 className="text-lg font-medium mb-3 uyghur-text">خەت چوڭلۇقى</h3>
          <div>
            <input
              type="range"
              min="0.8"
              max="1.4"
              step="0.1"
              value={settings.fontScale}
              onChange={handleFontScaleChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span className="uyghur-text">كىچىك</span>
              <span className="uyghur-text">ئوتتۇرا</span>
              <span className="uyghur-text">چوڭ</span>
            </div>
            <div className="mt-4 p-3 bg-gray-100 rounded-lg uyghur-text text-center" 
                 style={{ fontSize: `${settings.fontScale}rem` }}>
              بۇ خەتنىڭ چوڭلۇقىنى كۆرۈپ بېقىڭ
            </div>
          </div>
        </div>
        
        {/* Reset Button */}
        <div className="pt-4 border-t flex justify-end">
          <button
            onClick={resetSettings}
            className="btn btn-outline flex items-center gap-2"
          >
            <RefreshCw size={18} />
            <span className="uyghur-text">ئەسلىگە قايتۇرۇش</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;